// Validation regex patterns
export const VALIDATION_PATTERNS = {
  // Indian mobile: starts with 6-9, 10 digits
  MOBILE: /^[6-9]\d{9}$/,
  
  // GST format: 2-digit state code + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
  // Example: 27AAAAA0000A1Z5
  GST: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  
  // PAN format: 5 letters + 4 digits + 1 letter
  // Example: ABCDE1234F
  PAN: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  
  // Indian PIN: 6 digits, doesn't start with 0
  PIN_CODE: /^[1-9][0-9]{5}$/,
  
  // Email format
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  
  // Indian vehicle registration number: exactly 10 characters, no spaces or
  // hyphens — 2-letter state code + 2-digit RTO code + 2-letter series +
  // 4-digit number. Example: MH12AB1234
  VEHICLE_NUMBER: /^[A-Z]{2}[0-9]{2}[A-Z]{2}[0-9]{4}$/,

  // Aadhaar: 12 digits
  AADHAAR: /^\d{12}$/,
  
  // IFSC Code: 4 letters + 0 + 6 alphanumeric
  IFSC: /^[A-Z]{4}0[A-Z0-9]{6}$/,

  // Indian bank account number: 9-18 digits
  BANK_ACCOUNT_NUMBER: /^[0-9]{9,18}$/,

  // Legal/business name (company, bank, branch, tagline) — letters, digits,
  // spaces, and the punctuation those commonly contain (Pvt Ltd, LLP, & Co.)
  LEGAL_ENTITY_NAME: /^[A-Za-z0-9&.,'()/\- ]{1,255}$/,
  
  // Company Name: Alphabets and spaces only (max 100)
  COMPANY_NAME: /^[A-Za-z ]{1,100}$/,

  // Registration Number: Alphanumeric only (max 15)
  REGISTRATION_NUMBER: /^[A-Za-z0-9]{1,15}$/,

  // Address: Alphanumeric with comma, dot, dash (max 200)
  ADDRESS: /^[A-Za-z0-9 ,.-]{1,200}$/,

  // City: Alphabets and spaces only (max 100)
  CITY: /^[A-Za-z ]{1,100}$/,

  // Contact Person Name: Alphabets and spaces only (max 100)
  CONTACT_PERSON_NAME: /^[A-Za-z ]{1,100}$/,
  
  // Designation: Alphabets and spaces only (max 30)
  DESIGNATION: /^[A-Za-z ]{1,30}$/,
  
  // Only alphabets and spaces
  ALPHABETS_ONLY: /^[a-zA-Z\s]+$/,
  
  // Alphanumeric with spaces
  ALPHANUMERIC: /^[a-zA-Z0-9\s]+$/,
  
  // Numbers only
  NUMBERS_ONLY: /^\d+$/,
};

// Input sanitizers - allow only valid characters while typing
export const INPUT_SANITIZERS = {
  // Allow only digits
  digitsOnly: (value: string): string => {
    return value.replace(/\D/g, '');
  },
  
  // Allow only uppercase letters and digits for GST (max 15)
  gstFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15);
  },
  
  // Allow only uppercase letters and digits for PAN (exactly 10 characters)
  panFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 10);
  },

  // Indian bank account number — digits only, max 18
  bankAccountFormat: (value: string): string => {
    return value.replace(/\D/g, '').slice(0, 18);
  },

  // Legal/business name — letters, digits, spaces and common business
  // punctuation (Pvt Ltd, LLP, & Co.), collapses repeated spaces, max 255
  legalEntityNameFormat: (value: string): string => {
    return value.replace(/[^A-Za-z0-9&.,'()/\- ]/g, '').replace(/\s+/g, ' ').slice(0, 255);
  },
  
  // Indian vehicle number — uppercase letters/digits only, no spaces or
  // hyphens, capped at exactly 10 characters (MH12AB1234).
  vehicleFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  },

  // LR number — uppercase letters, digits and hyphens only, max 16
  // characters (LR-2026-001234 is 14).
  lrNumberFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 16);
  },
  
  // Allow only alphabets and spaces
  alphabetsOnly: (value: string): string => {
    return value.replace(/[^a-zA-Z\s]/g, '');
  },
  
  // Allow only alphanumeric and spaces
  alphanumeric: (value: string): string => {
    return value.replace(/[^a-zA-Z0-9\s]/g, '');
  },
  
  // Email - lowercase, allow valid email characters
  emailFormat: (value: string): string => {
    return value.toLowerCase().replace(/[^a-z0-9._%+-@]/g, '');
  },
  
  // IFSC code format
  ifscFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  },

  // Invoice number format - alphanumeric (uppercase), max 20 characters
  invoiceFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
  },

  // Driver name - prevent consecutive spaces, limit to 20 characters
  driverNameFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ').slice(0, 20);
  },

  // Remarks - alphanumeric and spaces only, max 200 characters
  remarksFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, ' ').slice(0, 200);
  },

  // Company name - alphabets and spaces only, max 100 characters
  companyNameFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ').slice(0, 100);
  },

  // Registration number - alphanumeric only, max 15 characters, uppercase
  registrationNumberFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
  },

  // Address - alphanumeric with comma, dot, dash, max 200 characters
  addressFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z0-9 ,.-]/g, '').replace(/\s+/g, ' ').slice(0, 200);
  },

  // City name - alphabets and spaces only, max 100 characters
  cityNameFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ').slice(0, 100);
  },

  // Contact person name - alphabets and spaces only, max 100 characters
  contactPersonNameFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ').slice(0, 100);
  },

  // Designation - alphabets and spaces only, max 30 characters
  designationFormat: (value: string): string => {
    return value.replace(/[^a-zA-Z\s]/g, '').replace(/\s+/g, ' ').slice(0, 30);
  },

  // Positive integer counts (master packing, pack size, qty/pallet, etc.) —
  // digits only, strips leading zeros as you type (e.g. "00005" -> "5"),
  // collapsing an all-zero run down to a single "0" so the field can still
  // be cleared/typed through naturally.
  positiveIntegerFormat: (value: string): string => {
    return value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  },

  // Lot number — letters, digits, hyphen and slash only (matches real-world
  // lot numbers like "MPP-0626/0304"), uppercase, max 20 characters.
  lotNoFormat: (value: string): string => {
    return value.toUpperCase().replace(/[^A-Z0-9/-]/g, '').slice(0, 20);
  },
};

// Validation error messages
export const VALIDATION_MESSAGES = {
  REQUIRED: (field: string) => `${field} is required`,
  INVALID_FORMAT: (field: string) => `${field} format is invalid`,
  MIN_LENGTH: (field: string, length: number) => `${field} must be at least ${length} characters`,
  MAX_LENGTH: (field: string, length: number) => `${field} must not exceed ${length} characters`,
  
  // Specific field messages
  COMPANY_NAME: 'Company Name should contain only alphabets',
  COMPANY_NAME_MAX: 'Maximum 100 characters allowed',
  REGISTRATION_NUMBER: 'Registration Number should be alphanumeric only',
  REGISTRATION_NUMBER_MAX: 'Maximum 15 characters allowed',
  GST: 'GST Number must be 15 characters in valid format',
  GST_INVALID: 'Invalid GST format. Example: 27AAAAA0000A1Z5',
  PAN: 'PAN Number must be 10 characters in valid format',
  PAN_INVALID: 'Invalid PAN format. Example: ABCDE1234F',
  ADDRESS: 'Address contains invalid characters',
  ADDRESS_MAX: 'Maximum 200 characters allowed',
  CITY: 'City should contain only alphabets',
  CITY_MAX: 'Maximum 100 characters allowed',
  CONTACT_PERSON_NAME: 'Contact Person Name should contain only alphabets',
  CONTACT_PERSON_NAME_MAX: 'Maximum 100 characters allowed',
  DESIGNATION: 'Designation should contain only alphabets',
  DESIGNATION_MAX: 'Maximum 30 characters allowed',
  EMAIL: 'Enter a valid email address',
  MOBILE: 'Enter a valid 10-digit mobile number',
  ALTERNATE_MOBILE: 'Enter a valid alternate mobile number',
  ALTERNATE_MOBILE_SAME: 'Alternate number cannot be same as primary mobile number',
  PIN_CODE: 'PIN code must be a valid 6-digit code',
  VEHICLE_NUMBER: 'Vehicle number must be exactly 10 characters in format: MH12AB1234',
  LR_NUMBER: 'Enter a valid LR Number (e.g., LR2026001234 or LR-2026-001234).',
  AADHAAR: 'Aadhaar number must be 12 digits',
  IFSC: 'IFSC code must be in format: ABCD0123456',
  DRIVER_NAME: 'Driver name can only contain letters and spaces (2-20 characters)',
  INVOICE_NUMBER: 'Invoice number can only contain letters and numbers (3-20 characters)',
  REMARKS: 'Remarks can only contain letters, numbers, and spaces (max 200 characters)',
};

// Field validators
export const validateField = (
  fieldName: string,
  value: string,
  pattern?: RegExp,
  minLength?: number,
  maxLength?: number,
  required: boolean = false
): string | null => {
  // Check if required
  if (required && !value.trim()) {
    return VALIDATION_MESSAGES.REQUIRED(fieldName);
  }
  
  // If not required and empty, no error
  if (!required && !value.trim()) {
    return null;
  }
  
  // Check min length
  if (minLength && value.trim().length < minLength) {
    return VALIDATION_MESSAGES.MIN_LENGTH(fieldName, minLength);
  }
  
  // Check max length
  if (maxLength && value.trim().length > maxLength) {
    return VALIDATION_MESSAGES.MAX_LENGTH(fieldName, maxLength);
  }
  
  // Check pattern
  if (pattern && !pattern.test(value.trim())) {
    return VALIDATION_MESSAGES.INVALID_FORMAT(fieldName);
  }
  
  return null;
};

// Specific field validators
export const FIELD_VALIDATORS = {
  gstNumber: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.GST;
    if (!value.trim()) return null;
    if (value.length !== 15) return VALIDATION_MESSAGES.GST;
    if (!VALIDATION_PATTERNS.GST.test(value)) return VALIDATION_MESSAGES.GST_INVALID;
    return null;
  },
  
  panNumber: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.PAN;
    if (!value.trim()) return null;
    if (value.length !== 10) return VALIDATION_MESSAGES.PAN;
    if (!VALIDATION_PATTERNS.PAN.test(value)) return VALIDATION_MESSAGES.PAN_INVALID;
    return null;
  },
  
  mobileNumber: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.MOBILE;
    if (!value.trim()) return null;
    if (value.length !== 10) return VALIDATION_MESSAGES.MOBILE;
    if (!VALIDATION_PATTERNS.MOBILE.test(value)) return VALIDATION_MESSAGES.MOBILE;
    return null;
  },
  
  pinCode: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.PIN_CODE;
    if (!value.trim()) return null;
    if (value.length !== 6) return VALIDATION_MESSAGES.PIN_CODE;
    if (!VALIDATION_PATTERNS.PIN_CODE.test(value)) return VALIDATION_MESSAGES.PIN_CODE;
    return null;
  },
  
  email: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.EMAIL;
    if (!value.trim()) return null;
    if (!VALIDATION_PATTERNS.EMAIL.test(value)) return VALIDATION_MESSAGES.EMAIL;
    return null;
  },
  
  vehicleNumber: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.VEHICLE_NUMBER;
    if (!value.trim()) return null;
    const cleaned = value.trim().toUpperCase();
    if (cleaned.length !== 10) return VALIDATION_MESSAGES.VEHICLE_NUMBER;
    if (!VALIDATION_PATTERNS.VEHICLE_NUMBER.test(cleaned)) {
      return VALIDATION_MESSAGES.VEHICLE_NUMBER;
    }
    return null;
  },
  
  lrNumber: (value: string, required: boolean = false): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.LR_NUMBER;
    return null;
  },

  aadhaar: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.AADHAAR;
    if (!value.trim()) return null;
    if (value.length !== 12) return VALIDATION_MESSAGES.AADHAAR;
    if (!VALIDATION_PATTERNS.AADHAAR.test(value)) return VALIDATION_MESSAGES.AADHAAR;
    return null;
  },
  
  ifscCode: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return VALIDATION_MESSAGES.IFSC;
    if (!value.trim()) return null;
    if (value.length !== 11) return VALIDATION_MESSAGES.IFSC;
    if (!VALIDATION_PATTERNS.IFSC.test(value)) return VALIDATION_MESSAGES.IFSC;
    return null;
  },

  driverName: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return 'Driver name is required';
    if (!value.trim()) return null;
    if (value.trim().length < 2) return 'Driver name must be at least 2 characters';
    if (value.trim().length > 20) return 'Driver name must not exceed 20 characters';
    if (!/^[a-zA-Z\s]+$/.test(value.trim())) return VALIDATION_MESSAGES.DRIVER_NAME;
    // Check for consecutive spaces
    if (/\s{2,}/.test(value.trim())) return 'Driver name cannot have consecutive spaces';
    return null;
  },

  invoiceNumber: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return 'Invoice number is required';
    if (!value.trim()) return null;
    return null;
  },

  remarks: (value: string, required: boolean = false): string | null => {
    if (required && !value.trim()) return 'Remarks are required';
    if (!value.trim()) return null;
    if (value.trim().length > 200) return 'Remarks must not exceed 200 characters';
    if (!/^[a-zA-Z0-9\s]*$/.test(value.trim())) return VALIDATION_MESSAGES.REMARKS;
    return null;
  },

  companyName: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return 'Company name is required';
    if (!value.trim()) return null;
    if (value.trim().length > 100) return VALIDATION_MESSAGES.COMPANY_NAME_MAX;
    if (!VALIDATION_PATTERNS.COMPANY_NAME.test(value.trim())) return VALIDATION_MESSAGES.COMPANY_NAME;
    if (/\s{2,}/.test(value)) return 'Company name cannot have consecutive spaces';
    return null;
  },

  registrationNumber: (value: string, required: boolean = false): string | null => {
    if (required && !value.trim()) return 'Registration number is required';
    if (!value.trim()) return null;
    if (value.trim().length > 15) return VALIDATION_MESSAGES.REGISTRATION_NUMBER_MAX;
    if (!VALIDATION_PATTERNS.REGISTRATION_NUMBER.test(value.trim())) return VALIDATION_MESSAGES.REGISTRATION_NUMBER;
    return null;
  },

  address: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return 'Address is required';
    if (!value.trim()) return null;
    if (value.trim().length > 200) return VALIDATION_MESSAGES.ADDRESS_MAX;
    if (!VALIDATION_PATTERNS.ADDRESS.test(value.trim())) return VALIDATION_MESSAGES.ADDRESS;
    if (/\s{2,}/.test(value)) return 'Address cannot have consecutive spaces';
    return null;
  },

  cityName: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return 'City is required';
    if (!value.trim()) return null;
    if (value.trim().length > 100) return VALIDATION_MESSAGES.CITY_MAX;
    if (!VALIDATION_PATTERNS.CITY.test(value.trim())) return VALIDATION_MESSAGES.CITY;
    if (/\s{2,}/.test(value)) return 'City name cannot have consecutive spaces';
    return null;
  },

  contactPersonName: (value: string, required: boolean = true): string | null => {
    if (required && !value.trim()) return 'Contact person name is required';
    if (!value.trim()) return null;
    if (value.trim().length > 100) return VALIDATION_MESSAGES.CONTACT_PERSON_NAME_MAX;
    if (!VALIDATION_PATTERNS.CONTACT_PERSON_NAME.test(value.trim())) return VALIDATION_MESSAGES.CONTACT_PERSON_NAME;
    if (/\s{2,}/.test(value)) return 'Contact person name cannot have consecutive spaces';
    return null;
  },

  designation: (value: string, required: boolean = false): string | null => {
    if (required && !value.trim()) return 'Designation is required';
    if (!value.trim()) return null;
    if (value.trim().length > 30) return VALIDATION_MESSAGES.DESIGNATION_MAX;
    if (!VALIDATION_PATTERNS.DESIGNATION.test(value.trim())) return VALIDATION_MESSAGES.DESIGNATION;
    if (/\s{2,}/.test(value)) return 'Designation cannot have consecutive spaces';
    return null;
  },
};

// Format display values
export const formatDisplayValue = {
  vehicleNumber: (value: string): string => {
    // Format as: MH-02-AB-1234
    const cleaned = value.replace(/[\s-]/g, '');
    if (cleaned.length >= 4) {
      let formatted = cleaned.substring(0, 2);
      if (cleaned.length >= 4) formatted += '-' + cleaned.substring(2, 4);
      if (cleaned.length >= 6) formatted += '-' + cleaned.substring(4, 6);
      if (cleaned.length > 6) formatted += '-' + cleaned.substring(6);
      return formatted;
    }
    return cleaned;
  },
  
  aadhaar: (value: string): string => {
    // Format as: 1234-5678-9012
    const cleaned = value.replace(/[\s-]/g, '');
    if (cleaned.length > 4) {
      let formatted = cleaned.substring(0, 4);
      if (cleaned.length > 4) formatted += '-' + cleaned.substring(4, 8);
      if (cleaned.length > 8) formatted += '-' + cleaned.substring(8, 12);
      return formatted;
    }
    return cleaned;
  },
};
